import { useState } from 'react';
import { DoorOpen, X, Loader2, KeyRound, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRemaining } from '@/lib/time';
import type { Device, DeviceAssignment, AccessKey } from '@/types';

interface DeviceTileProps {
  assignment: DeviceAssignment;
  /** Active keys for this device — drives the status row. */
  activeKeys?: AccessKey[];
  /** Highlight ring for the currently selected device. */
  isSelected?: boolean;
  onOpen: (assignment: DeviceAssignment) => void;
  onRemove?: (assignmentId: string) => void;
  isRemoving?: boolean;
}

/**
 * DeviceTile — full-bredds radkort för en resurs (enhet).
 *
 * Horisontell glasrad (ikon · namn/status · badges · chevron/×) i linje med
 * "Skicka nyckel"-sidans enhetslista. Används i Nycklar- och Enheter-
 * listorna — alla kort i appen är full bredd. En enhet med aktiv nyckel får
 * en starkare yta + status-dot.
 */
export function DeviceTile({
  assignment,
  activeKeys = [],
  isSelected,
  onOpen,
  onRemove,
  isRemoving,
}: DeviceTileProps) {
  const device: Device | undefined = assignment.device;
  const [showConfirm, setShowConfirm] = useState(false);
  if (!device) return null;

  const hasKey = activeKeys.length > 0;
  const soonestExpiry = hasKey
    ? Math.min(...activeKeys.map((k) => new Date(k.expires_at).getTime()))
    : null;
  const remaining = soonestExpiry ? soonestExpiry - Date.now() : null;

  const hasTimeRestriction = assignment.access_start_time || assignment.access_end_time;

  const keyStatusLabel =
    hasKey && remaining !== null
      ? formatRemaining(remaining)
      : hasTimeRestriction
        ? `${assignment.access_start_time?.slice(0, 5)}–${assignment.access_end_time?.slice(0, 5)}`
        : 'Tryck för nyckel';

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onRemove?.(assignment.id);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
    }
  };

  return (
    <button
      onClick={() => onOpen(assignment)}
      className={cn(
        'group relative flex w-full items-center gap-3 rounded-2xl p-4 text-left transition-all active:scale-[0.98]',
        hasKey ? 'glass-regular' : 'glass-thin',
        isSelected && 'ring-2 ring-[hsl(var(--ring))]',
        isRemoving && 'opacity-50 scale-95'
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
        <DoorOpen className="h-5 w-5 text-foreground" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-semibold leading-tight tracking-tight">{device.name}</p>
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              hasKey
                ? 'bg-foreground shadow-[0_0_8px_hsl(var(--foreground)/0.7)]'
                : 'bg-muted-foreground/40'
            )}
          />
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
          <KeyRound className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {keyStatusLabel}
            {device.location && ` · ${device.location}`}
          </span>
        </p>
      </div>

      <span className="flex shrink-0 gap-1">
        {assignment.source === 'facility' && (
          <span className="rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))] text-foreground/60">
            Anläggning
          </span>
        )}
        {device.qr_enabled && (
          <span className="rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))] text-foreground/80">
            QR
          </span>
        )}
        {device.nfc_enabled && (
          <span className="rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))] text-foreground/80">
            NFC
          </span>
        )}
      </span>

      {onRemove ? (
        <span
          role="button"
          tabIndex={0}
          onClick={handleRemove}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleRemove(e as unknown as React.MouseEvent);
          }}
          onBlur={() => setShowConfirm(false)}
          className={cn(
            'shrink-0 rounded-md p-1 transition-all',
            showConfirm
              ? 'bg-destructive/10 text-destructive'
              : 'text-muted-foreground hover:bg-[hsl(var(--foreground)/0.08)] hover:text-foreground'
          )}
          aria-label={showConfirm ? 'Bekräfta borttagning' : 'Ta bort enhet'}
        >
          {isRemoving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </span>
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}

      {showConfirm && (
        <span className="absolute -top-6 right-2 animate-fade-in rounded border border-destructive/20 bg-background px-1.5 py-0.5 text-[10px] text-destructive shadow-sm">
          Tryck igen för att ta bort
        </span>
      )}
    </button>
  );
}
